import { useEffect, useSyncExternalStore } from "react";

// Tiny module-level store. Keeps the overlay completely independent of
// React context plumbing so any server-fn caller (sign-in handler,
// /dashboard redirect, future auth flows) can show it from a plain
// async function with no provider boilerplate.
type State = { active: boolean; since: number };
let state: State = { active: false, since: 0 };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function getSnapshot(): State {
  return state;
}

// SSR snapshot must be a STABLE reference (same object every call) or React
// 19 logs "The result of getServerSnapshot should be cached to avoid an
// infinite loop". Cache it at module scope.
const SERVER_SNAPSHOT: State = { active: false, since: 0 };
function getServerSnapshot(): State {
  return SERVER_SNAPSHOT;
}

/**
 * Show / hide the global auth loading overlay. Safe to call from anywhere.
 * Idempotent. When activating, stamps `since` so the overlay can self-clear
 * if a caller forgets to (preventing the "stuck on splash after login" bug).
 */
export function setAuthLoading(active: boolean) {
  if (state.active === active) return;
  state = { active, since: active ? Date.now() : 0 };
  emit();
}

export async function withAuthLoading<T>(fn: () => Promise<T>): Promise<T> {
  setAuthLoading(true);
  try {
    return await fn();
  } finally {
    setAuthLoading(false);
  }
}

export function AuthLoadingOverlay() {
  const { active, since } = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Safety net: regardless of who toggles it, never let the overlay sit
  // longer than 4s. If the destination route is still loading by then the
  // user will see the route's own pending UI — far better than a frozen
  // splash that hides the whole app.
  useEffect(() => {
    if (!active) return;
    const elapsed = Date.now() - since;
    const remaining = Math.max(0, 4000 - elapsed);
    const t = window.setTimeout(() => setAuthLoading(false), remaining);
    return () => window.clearTimeout(t);
  }, [active, since]);

  if (!active) return null;
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      role="status"
      className="fixed inset-0 z-[200] bg-background"
    >
      <span className="sr-only">Signing you in…</span>
      {/* Discreet skeleton so the screen still feels alive while the
          destination route's data loads. Mirrors the common app-shell:
          slim top bar, a heading line, two content blocks. */}
      <div className="mx-auto flex h-full max-w-5xl flex-col gap-6 px-6 py-8">
        <div className="flex items-center justify-between">
          <div className="h-6 w-32 animate-pulse rounded bg-muted" />
          <div className="h-6 w-6 animate-pulse rounded-full bg-muted" />
        </div>
        <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-muted/70" />
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="h-32 animate-pulse rounded-lg bg-muted/60" />
          <div className="h-32 animate-pulse rounded-lg bg-muted/60" />
        </div>
        <div className="h-48 animate-pulse rounded-lg bg-muted/50" />
      </div>
    </div>
  );
}

